import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Delete02Icon,
  Exchange01Icon,
  UploadCircle01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import { IDV_OVERALL_STATUS, IDV_OVERALL_STATUS_DISPLAY, MAX_RETRIES } from 'constants/idv';
import {
  ALLOWED_TYPES_MODE_ID,
  ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ALLOWED_TYPES_MODE_STANDARD,
} from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { uploadSigned } from 'helpers/aws-s3';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import {
  extractFieldCodes,
  getBorderStatus,
  handleVerificationResponse,
  validateRetryCount,
  verifyIdDocument,
} from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { parseIdKey } from 'helpers/ids';
import { getUploadAcceptConfig } from 'helpers/upload-allowed-types';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelfieModal } from 'views/CandidateApp/CandidateForm/PreSubsectionFlows/components/Selfie/SelfieModal/SelfieModal';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ExtractedFieldsDisplay } from 'components/ExtractedFieldsDisplay/ExtractedFieldsDisplay';
import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import { getCandidateApplication } from 'api/candidate-application-slice';

import styles from './DocumentUpload.module.scss';

export const DualUploadComponent = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  additionalInstructions = '',
  frontTitle = 'Front',
  backTitle = 'Back',
  uploadCode,
  selfieId,
  reTakeSelfie,
  frontValue,
  backValue,
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder = FilesFolders.CandidatesApplicationDocuments,
  maxSize = 20971520,
  caption = '',
  accept,
  allowedTypesMode = ALLOWED_TYPES_MODE_STANDARD,
  idType,
  brand,
  useModel = false,
  disabled = false,
  verifyDocument = false,
  rejected = false,
  expiryTolerance = null,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  displayFields = [],
  uploadError = '',
  uploadStatus = '',
  idValues = {},
  backConditions = null,
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  handleStateOnVerify = () => {},
  confirmedByCand = false,
}) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { verify: verifyByToken } = useVerifyIdDocumentByToken(token || undefined);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();

  const idUploadTranslations = t(`idUpload`, { ns: 'id_document_types', returnObjects: true });

  const { effectiveAccept, allowedLabel, isFileAllowed } = getUploadAcceptConfig(
    allowedTypesMode,
    idType,
    accept,
    t,
  );

  // ─── File state ───────────────────────────────────────────────────────────
  const [frontFileName, setFrontFileName] = useState(null);
  const [frontUploadId, setFrontUploadId] = useState(null);
  const [frontLoading, setFrontLoading] = useState(false);

  const [backFileName, setBackFileName] = useState(null);
  const [backUploadId, setBackUploadId] = useState(null);
  const [backLoading, setBackLoading] = useState(false);

  // ─── Verification state ───────────────────────────────────────────────────
  const [verifying, setVerifying] = useState(false);
  const [verifyingFront, setVerifyingFront] = useState(false);
  const [frontVerified, setFrontVerified] = useState(false);
  const [status, setStatus] = useState(uploadStatus);
  const [error, setError] = useState(() => {
    if (!uploadError) return '';
    const failureKey =
      Array.isArray(uploadError) && uploadError.length > 0
        ? uploadError[0]
        : typeof uploadError === 'string'
          ? uploadError
          : null;
    return failureKey && idUploadTranslations?.reasonForFailure?.[failureKey]
      ? idUploadTranslations.reasonForFailure[failureKey]
      : failureKey || '';
  });
  const [errorType, setErrorType] = useState(MessageType.Error);
  const [verificationError, setVerificationError] = useState(null);
  const [isFileRejected, setIsFileRejected] = useState(rejected);

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [expand, setExpand] = useState(!confirmedByCand);
  const [isConfirmed, setIsConfirmed] = useState(confirmedByCand);
  const [showSelfieModal, setShowSelfieModal] = useState(false);

  const brandingStyleVariables = useBrandingStyleVariables(brand);

  // ─── Attempt count (state so render-time reads stay reactive) ────────────
  const [currentAttempts, setCurrentAttempts] = useState(metadata?.attempts || 0);

  // ─── Refs (minimal) ───────────────────────────────────────────────────────
  // Set to true only when handleFileUpload creates a new record; guards against
  // verification firing on initial prop→state initialization
  const justUploadedRef = useRef(false);
  // Prevent duplicate verification calls within the same upload cycle
  const frontVerifyCalledRef = useRef(false);
  const dualVerifyCalledRef = useRef(false);
  // Prevent re-initialization with same prop values
  const initializedRef = useRef({ front: null, back: null });

  const backConditionsExists = Object.keys(backConditions || {}).length > 0;

  // ─── Derived values ───────────────────────────────────────────────────────
  const bothUploadsComplete = frontUploadId !== null && backUploadId !== null;

  const shouldHideUploads =
    currentAttempts >= MAX_RETRIES &&
    (uploadStatus === IDV_OVERALL_STATUS.FAILED ||
      uploadStatus === IDV_OVERALL_STATUS.REJECTED ||
      uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW);

  const borderStatus = useMemo(() => getBorderStatus(status), [status]);

  const backConditionsMet = useMemo(() => {
    if (!backConditionsExists) return true;
    return evaluateFieldConditions(backConditions, idValues, {});
  }, [backConditionsExists, backConditions, idValues]);

  // ─── Single callOnChange helper ───────────────────────────────────────────
  const callOnChange = useCallback(
    (frontId, backId) => {
      onChange({ front: frontId, back: backId });
    },
    [onChange],
  );

  // ─── Sync uploadStatus prop → local status ────────────────────────────────
  useEffect(() => {
    setStatus(uploadStatus);
    if (backConditionsExists && frontUploadId) {
      // Restore frontVerified when back was already uploaded in a prior session (backValue present)
      // so the back box stays visible on reload regardless of uploadStatus
      if (uploadStatus === IDV_OVERALL_STATUS.VERIFIED || backValue) {
        setFrontVerified(true);
      }
    }
  }, [uploadStatus, backConditionsExists, frontUploadId, backValue]);

  // Sync metadata.attempts prop → currentAttempts (parent may send updated attempts)
  useEffect(() => {
    if (metadata?.attempts !== undefined) {
      setCurrentAttempts(prev => (metadata.attempts > prev ? metadata.attempts : prev));
    }
  }, [metadata?.attempts]);

  // ─── Initialize from props ────────────────────────────────────────────────
  useEffect(() => {
    const initSide = async (value, setFileName, setUploadIdFn, side) => {
      const valueId = useModel ? value?.id : value;
      // eslint-disable-next-line eqeqeq
      if (initializedRef.current[side] == valueId && initializedRef.current[side] !== null) return;
      initializedRef.current[side] = valueId;

      if (!value) {
        setFileName(null);
        setUploadIdFn(null);
        return;
      }
      if (useModel) {
        setFileName(value.file_name);
        setUploadIdFn(value.id);
      } else {
        try {
          const upload = await getUpload(value);
          if (upload) {
            setFileName(upload.file_name);
            setUploadIdFn(upload.id);
          }
        } catch {
          // Transient error — prop value still exists, keep existing state
        }
      }
    };

    initSide(frontValue, setFrontFileName, setFrontUploadId, 'front');
    initSide(backValue, setBackFileName, setBackUploadId, 'back');
  }, [frontValue, backValue, useModel, getUpload]);

  // ─── Shared verification callbacks builder ────────────────────────────────
  const buildVerificationCallbacks = useCallback(
    overrideOnChange => ({
      setStatus,
      setError,
      setErrorType,
      setVerificationError,
      setShowSelfieModal,
      setIsFileRejected,
      reTakeSelfie,
      onChange: overrideOnChange,
      validateRetryCount: (upload, callbacks, um, st) =>
        validateRetryCount(
          upload,
          { setStatus, setError, setErrorType, onChange: overrideOnChange, setIsFileRejected },
          um,
          st,
        ),
    }),
    [reTakeSelfie],
  );

  // ─── Front-only verification (backConditionsExists path) ──────────────────
  const triggerFrontOnlyVerification = useCallback(
    async frontId => {
      if (frontVerifyCalledRef.current) return;
      if (!verifyDocument || !token || !frontId) return;
      if (status === IDV_OVERALL_STATUS.VERIFIED || uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
        setFrontVerified(true);
        return;
      }
      const idCode = metadata?.id_type || null;
      if (!idCode) return;

      frontVerifyCalledRef.current = true;
      setVerifyingFront(true);
      setError(null);
      setVerificationError(null);
      handleStateOnVerify(true);
      disableFormActions();

      const onChangeForFront = () =>
        callOnChange(useModel ? frontValue : frontId, useModel ? backValue : backUploadId || null);
      const callbacks = buildVerificationCallbacks(onChangeForFront);

      try {
        const result = await verifyIdDocument(
          {
            uploadId: frontId,
            metadata,
            listCode,
            expiryTolerance,
            verifyByToken,
            reTakeSelfie,
            getUpload,
            useModel,
            value: useModel ? frontValue : frontId,
            skipVerification,
          },
          { ...callbacks, setVerifying: setVerifyingFront },
        );

        if (result.success) {
          setIsFileRejected(false);
          setFrontVerified(true);
          onChangeForFront();
        } else {
          // Allow retry on next upload
          frontVerifyCalledRef.current = false;
        }
      } catch (err) {
        console.error('Error verifying front document:', err);
        setVerificationError(err?.message || 'Verification failed');
        const upload = useModel ? frontValue : await getUpload(frontId);
        validateRetryCount(
          upload,
          { setStatus, setError, setErrorType, onChange: onChangeForFront, setIsFileRejected },
          useModel,
          null,
        );
        frontVerifyCalledRef.current = false;
      } finally {
        setVerifyingFront(false);
        handleStateOnVerify(false);
        enableFormActions();
        justUploadedRef.current = false;
      }
    },
    [
      verifyDocument,
      token,
      status,
      uploadStatus,
      metadata,
      listCode,
      expiryTolerance,
      verifyByToken,
      reTakeSelfie,
      getUpload,
      useModel,
      frontValue,
      backValue,
      backUploadId,
      skipVerification,
      callOnChange,
      buildVerificationCallbacks,
      handleStateOnVerify,
      disableFormActions,
      enableFormActions,
    ],
  );

  // ─── Dual verification (both front + back) ────────────────────────────────
  const triggerDualVerification = useCallback(
    async (frontId, backId) => {
      if (dualVerifyCalledRef.current) return;
      if (!verifyDocument || !token || !frontId || !backId) return;
      if (status === IDV_OVERALL_STATUS.VERIFIED || uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
        return;
      }
      const idCode = metadata?.id_type || null;
      if (!idCode) return;

      dualVerifyCalledRef.current = true;
      setVerifying(true);
      setError(null);
      setVerificationError(null);
      handleStateOnVerify(true);
      disableFormActions();

      const onChangeBoth = () =>
        callOnChange(useModel ? frontValue : frontId, useModel ? backValue : backId);
      const callbacks = buildVerificationCallbacks(onChangeBoth);

      try {
        const { idType, countryCode } = parseIdKey(idCode);
        const data = await verifyByToken({
          listCode,
          uploadId: frontId,
          backUploadId: backId,
          idCode,
          idType,
          idCountry: countryCode || null,
          expiryTolerance,
        });

        const result = await handleVerificationResponse(
          data,
          {
            verifyByToken,
            getUpload,
            metadata,
            listCode,
            expiryTolerance,
            reTakeSelfie,
            useModel,
            value: useModel ? frontValue : frontId,
            uploadId: frontId,
          },
          callbacks,
        );

        if (result.success) {
          setIsFileRejected(false);
          onChangeBoth();
        } else {
          // Allow retry
          dualVerifyCalledRef.current = false;
        }
      } catch (err) {
        console.error('Error verifying dual document:', err);
        setVerificationError(err?.message || 'Verification failed');
        const upload = useModel ? frontValue : await getUpload(frontId);
        validateRetryCount(
          upload,
          { setStatus, setError, setErrorType, onChange: onChangeBoth, setIsFileRejected },
          useModel,
          null,
        );
        dualVerifyCalledRef.current = false;
      } finally {
        setVerifying(false);
        handleStateOnVerify(false);
        enableFormActions();
        justUploadedRef.current = false;
      }
    },
    [
      verifyDocument,
      token,
      status,
      uploadStatus,
      metadata,
      listCode,
      expiryTolerance,
      verifyByToken,
      reTakeSelfie,
      getUpload,
      useModel,
      frontValue,
      backValue,
      callOnChange,
      buildVerificationCallbacks,
      handleStateOnVerify,
      disableFormActions,
      enableFormActions,
    ],
  );

  // ─── Unified verification trigger (single effect) ─────────────────────────
  useEffect(() => {
    // Only trigger if a file was just uploaded — not on initial prop→state initialization
    if (!justUploadedRef.current) return;

    // Case A: backConditionsExists — verify front only first to unlock back upload
    if (backConditionsExists && frontUploadId && !frontVerified && !frontVerifyCalledRef.current) {
      triggerFrontOnlyVerification(frontUploadId);
      return;
    }

    // Case B: standard dual — verify when both uploads are ready
    if (frontUploadId && backUploadId && !dualVerifyCalledRef.current) {
      // If backConditionsExists, front must be verified before dual verify
      if (backConditionsExists && !frontVerified) return;
      triggerDualVerification(frontUploadId, backUploadId);
    }
  }, [
    frontUploadId,
    backUploadId,
    frontVerified,
    backConditionsExists,
    triggerFrontOnlyVerification,
    triggerDualVerification,
  ]);

  // ─── File upload handler ──────────────────────────────────────────────────
  const handleFileUpload = async (file, isFront) => {
    disableFormActions();
    const setLoading = isFront ? setFrontLoading : setBackLoading;
    const setFileName = isFront ? setFrontFileName : setBackFileName;
    const setUploadIdFn = isFront ? setFrontUploadId : setBackUploadId;
    const currentValue = isFront ? frontValue : backValue;

    setLoading(true);
    handleStateOnVerify(true);
    setError(null);
    setVerificationError(null);

    const prevUploadId = useModel ? currentValue?.id : currentValue;

    try {
      // 1. Sign upload
      const signedFile = await signUpload({
        file_name: file.name,
        file_type: file.type,
        file_ext:
          mime.extension(file.type) ||
          (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
          'bin',
        bucket_folder: bucketFolder,
      });

      // 2. Upload to S3
      await uploadSigned(signedFile.url, file, file.type);

      // 3. Increment attempt counter (front upload only; back uses same count)
      let attempts = currentAttempts || 1;
      if (isFront) {
        attempts = currentAttempts === 0 ? 1 : currentAttempts + 1;
        setCurrentAttempts(attempts);
      }

      // 4. Create upload record
      const upload = await createUpload({
        ...modelParams,
        metadata: { ...metadata, attempts, dual: isFront ? 'front' : 'back' },
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      });

      // 5. Delete old uploads if user is retrying (both sides)
      if (prevUploadId && prevUploadId !== upload.id) {
        const oldFrontId = isFront ? frontUploadId : useModel ? frontValue?.id : frontValue;
        const oldBackId = isFront ? (useModel ? backValue?.id : backValue) : backUploadId;

        if (oldFrontId) {
          try {
            await deleteUpload(oldFrontId);
          } catch (e) {
            console.error('Failed to delete old front upload:', e);
          }
          if (!isFront) {
            setFrontFileName(null);
            setFrontUploadId(null);
          }
        }
        if (oldBackId) {
          try {
            await deleteUpload(oldBackId);
          } catch (e) {
            console.error('Failed to delete old back upload:', e);
          }
          if (isFront) {
            setBackFileName(null);
            setBackUploadId(null);
          }
        }
      }

      // 6. Reset verification flags — new upload cycle starts
      frontVerifyCalledRef.current = false;
      dualVerifyCalledRef.current = false;
      if (isFront) {
        setFrontVerified(false);
      }
      setStatus(null);
      setIsFileRejected(false);

      // 7. Mark that a real upload just happened (enables verification effect)
      justUploadedRef.current = true;

      // 8. Update local state and call onChange
      setFileName(upload.file_name);
      setUploadIdFn(upload.id);

      if (isFront) {
        const backToPass =
          prevUploadId && prevUploadId !== upload.id ? null : useModel ? backValue : backUploadId;
        callOnChange(useModel ? upload : upload.id, backToPass);
      } else {
        const frontToPass =
          prevUploadId && prevUploadId !== upload.id ? null : useModel ? frontValue : frontUploadId;
        callOnChange(frontToPass, useModel ? upload : upload.id);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      const message = err?.data?.message ?? err?.message ?? 'Upload failed. Please try again.';
      setError(message);
      setVerificationError(null);
      setErrorType(MessageType.Error);
    } finally {
      setLoading(false);
      enableFormActions();
      handleStateOnVerify(false);
    }
  };

  // ─── File delete handler ──────────────────────────────────────────────────
  const handleFileDelete = async (id, isFront) => {
    if (id) {
      const setLoading = isFront ? setFrontLoading : setBackLoading;
      setLoading(true);
      try {
        const extractedCodes = extractFieldCodes(displayFields);
        await deleteUpload(parseInt(id), extractedCodes.length > 0 ? extractedCodes : null);
      } catch {
        /* ignore delete errors */
      }
      (isFront ? setFrontLoading : setBackLoading)(false);
    }

    if (isFront) {
      setFrontFileName(null);
      setFrontUploadId(null);
      setFrontVerified(false);
      frontVerifyCalledRef.current = false;
      callOnChange(null, useModel ? backValue : backUploadId);
    } else {
      setBackFileName(null);
      setBackUploadId(null);
      callOnChange(useModel ? frontValue : frontUploadId, null);
    }

    // Reset verification state
    dualVerifyCalledRef.current = false;
    justUploadedRef.current = false;
    setStatus(null);
    setError(null);
    setVerificationError(null);
    setIsFileRejected(false);
  };

  // ─── Input handlers ───────────────────────────────────────────────────────
  const handleFileInput = (event, isFront) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > maxSize) {
      setError(
        `File is too large. Max size is ${maxSize / 1024 / 1024}MB. Please upload a smaller file.`,
      );
      setVerificationError(null);
      setErrorType(MessageType.Error);
      event.target.value = '';
      return;
    }
    if (!isFileAllowed(file)) {
      setError(`This file type is not supported. Please use ${allowedLabel}.`);
      setVerificationError(null);
      setErrorType(MessageType.Error);
      event.target.value = '';
      return;
    }
    handleFileUpload(file, isFront);
  };

  const handleReplace = isFront => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = effectiveAccept;
    input.onchange = event => handleFileInput(event, isFront);
    input.click();
  };

  // ─── Selfie upload ────────────────────────────────────────────────────────
  const uploadSelfie = useCallback(
    async photo => {
      await selfieHelper({
        photo,
        candidate: { id: uploadCode },
        signUpload,
        createUpload,
      });
      if (selfieId) {
        await deleteUpload(selfieId);
      }
      if (token) {
        await dispatch(getCandidateApplication(token));
      }
      setShowSelfieModal(false);
    },
    [token, uploadCode, signUpload, createUpload, selfieId, deleteUpload, dispatch],
  );

  // ─── Render upload box ────────────────────────────────────────────────────
  const renderUploadBox = (isFront, fileName, loading, currentUploadId, value) => {
    const title = isFront ? frontTitle : backTitle;
    const resolvedId = value || currentUploadId;

    if (shouldHideUploads) return null;

    return (
      <div
        className={cn(
          'u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium',
          loading ? 'u-border-in-progress' : fileName ? borderStatus : '',
        )}>
        <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
          <div className="d-flex flex-column u-gap u-max-upload-card-width">
            {caption && (
              <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
            )}
            <div className="d-flex flex-column u-gap-x-small">
              <span className="u-font-size-large u-font-weight-500">{`${documentTitle} - ${title}`}</span>
              {documentDescription && (
                <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                  {`${documentDescription} - ${title}`}
                </span>
              )}
              <span className="text-lg text-neutral-6">Max size: {maxSize / 1024 / 1024}MB</span>
              {additionalInstructions && (
                <span
                  className={cn(
                    'secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line',
                    documentDescription && 'u-margin-top--small',
                  )}>
                  {additionalInstructions}
                </span>
              )}
            </div>
            {bothUploadsComplete && (
              <div className="d-flex align-items-center u-gap">
                {isFileRejected ? (
                  <StatusPill text="Rejected" status="error" />
                ) : (
                  <>
                    {status === IDV_OVERALL_STATUS.FAILED &&
                      !isOnLastAttempt &&
                      uploadStatus === IDV_OVERALL_STATUS.FAILED && (
                        <StatusPill
                          text={IDV_OVERALL_STATUS_DISPLAY[status].title}
                          status={IDV_OVERALL_STATUS_DISPLAY[status].color}
                        />
                      )}
                    {status !== IDV_OVERALL_STATUS.FAILED &&
                      status !== IDV_OVERALL_STATUS.REJECTED &&
                      !error && (
                        <>
                          <StatusPill
                            text={
                              loading ? 'Uploading...' : fileName ? 'Uploaded' : 'Not Yet Uploaded'
                            }
                            status={loading ? 'warning' : fileName ? 'success' : 'neutral'}
                          />
                          {status === IDV_OVERALL_STATUS.VERIFIED && (
                            <StatusPill text="Verified" status="success" />
                          )}
                        </>
                      )}
                  </>
                )}
              </div>
            )}
          </div>

          {fileName ? (
            isConfirmed ? (
              <StatusPill text="Document Approved" status="success" />
            ) : (
              !disabled &&
              !isOnLastAttempt && (
                <div className="d-flex align-items-center u-gap">
                  <Button
                    type="secondary"
                    edged
                    onClick={() => handleReplace(isFront)}
                    disabled={loading || verifying || verifyingFront}
                    style={brandingStyleVariables}>
                    <HugeiconsIcon
                      size="20"
                      icon={Exchange01Icon}
                      strokeWidth="1.5"
                      className="u-margin-right--small"
                    />
                    <span>Replace</span>
                  </Button>
                  <IconComponent
                    icon={Delete02Icon}
                    onClick={() => handleFileDelete(resolvedId, isFront)}
                    tip="Delete"
                    type="error"
                    size="small"
                    noBackground
                  />
                </div>
              )
            )
          ) : (
            <label
              className={cn(styles.uploadButton, 'u-border', {
                'u-disabled': loading || verifying || verifyingFront || disabled,
              })}
              style={{
                '--brand-button-color': brandingStyleVariables['--button-color'],
                '--brand-button-text-color': brandingStyleVariables['--button-text-color'],
              }}>
              <input
                type="file"
                accept={effectiveAccept}
                onChange={event => handleFileInput(event, isFront)}
                disabled={loading || verifying || verifyingFront || disabled}
                style={{ display: 'none' }}
              />
              <HugeiconsIcon size="20" icon={UploadCircle01Icon} strokeWidth="1.5" />
              <span className="u-font-size-small u-font-weight-400 primary-text">
                {loading ? 'Uploading...' : 'Upload'}
              </span>
            </label>
          )}
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const isOnLastAttempt = currentAttempts >= MAX_RETRIES;
  const showExtractedFields =
    !frontLoading &&
    !backLoading &&
    !verifying &&
    !verifyingFront &&
    displayFields?.length > 0 &&
    frontFileName &&
    backFileName &&
    (uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
      uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
      uploadStatus === IDV_OVERALL_STATUS.NOT_APPLICABLE ||
      (uploadStatus === IDV_OVERALL_STATUS.FAILED && isOnLastAttempt));

  return (
    <>
      <SelfieModal
        show={showSelfieModal}
        brand={brand}
        screenName="camera"
        noClose
        title="Retake selfie"
        description="We ran into a little issue verifying your ID with the photo you provided. Could you take a clearer picture for us? Make sure your face is fully visible and well-lit."
        onSave={photo => uploadSelfie(photo)}
      />
      <div
        className={cn(
          styles.root,
          { [className]: className, 'u-disabled': disabled },
          'd-flex flex-column u-gap-medium u-padding u-border u-border-radius--medium',
        )}
        style={brandingStyleVariables}>
        <div className="d-flex flex-column u-gap-medium">
          <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
            <span className="u-font-size-large u-font-weight-500">{documentTitle}</span>
            {showExtractedFields && !disabled && (
              <IconComponent
                icon={expand ? ArrowUp01Icon : ArrowDown01Icon}
                noBackground
                type="secondary"
                size="medium"
                strokeWidth={1.5}
                onClick={() => setExpand(!expand)}
              />
            )}
          </div>

          {renderUploadBox(true, frontFileName, frontLoading, frontUploadId, frontValue)}

          {(!backConditionsExists || (backConditionsMet && frontVerified)) &&
            renderUploadBox(false, backFileName, backLoading, backUploadId, backValue)}
        </div>

        {needsComplementaryDoc && complementaryInstructions && (
          <DisplayBox show={true} alignLeft>
            <span className="u-font-size-small u-font-weight-400">{complementaryInstructions}</span>
          </DisplayBox>
        )}

        {error && <FormMessage message={error} type={errorType} noMargin closeable={false} />}

        {showExtractedFields && expand && (
          <div className="u-padding u-border u-border-radius--medium">
            <ExtractedFieldsDisplay
              displayFields={displayFields}
              idValues={idValues}
              brand={brand}
              uploadId={uploadId || frontUploadId}
              confirmedByCand={confirmedByCand}
              onConfirm={() => {
                setIsConfirmed(true);
                setExpand(false);
              }}
            />
          </div>
        )}

        {(verifying || verifyingFront) && (
          <FormMessage
            message={verifyingFront ? 'Verifying front document...' : 'Verifying documents...'}
            type="Info"
            noMargin
            closeable={false}
          />
        )}
      </div>
    </>
  );
};

DualUploadComponent.propTypes = {
  documentTitle: PropTypes.string,
  frontTitle: PropTypes.string,
  backTitle: PropTypes.string,
  frontValue: PropTypes.any,
  backValue: PropTypes.any,
  modelParams: PropTypes.object,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  allowedTypesMode: PropTypes.oneOf([
    ALLOWED_TYPES_MODE_STANDARD,
    ALLOWED_TYPES_MODE_ID,
    ALLOWED_TYPES_MODE_IMAGE_ONLY,
  ]),
  idType: PropTypes.string,
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
};
